import { useState, useEffect } from 'react';
import {
  CANVAS_SIZE,
  NODE_W, NODE_H, NODE_W_ROOT, NODE_H_ROOT,
} from '../constants';
import { useConnectInteraction } from '../hooks/useConnectInteraction';
import { useMobileInteractions } from '../hooks/useMobileInteractions';
import { CanvasToolbar } from './CanvasToolbar';
import { ConnectionItem, ConnectionOverlay } from './ConnectionItem';
import { NodeItem } from './NodeItem';
import { Modal } from './Modal';
import { PostItItem } from './PostItItem';
import { useT } from '../i18n';
import { stripFlashcardMarkers } from '../utils/storage';

const maskTitleTerms = (text = '', title = '') => {
  if (!text || !title) return text;
  let masked = text;
  const words = title.split(/[\s,.-]+/).map(w => w.trim()).filter(w => w.length > 2);
  words.forEach((word) => {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    masked = masked.replace(new RegExp(escaped, 'gi'), '____');
  });
  return masked;
};

export const Canvas = ({
  nodes, connections,
  scrollContainerRef, svgRef,
  selectionState, selectedNodes, selectedNode, hoveredNode,
  onNodeMouseDown, onNodeClick, onNodeHover, onCanvasMouseDown,
  onToggleBan, onFocusNode, onMarkNode, onEditNode, onColorNode,
  onSuggestSolutions, onDeleteNode, onStartInfoGathering,
  onCenterCanvas, onExportPNG, onExportMermaid, onExportJSON,
  isPanMode, onTogglePanMode,
  onBulkToggleBan, onBulkDelete, onBulkColorChange,
  onAddNode, activeParentNode,
  onAnalyzeMap, onShareMap, hasNodes, isAnalyzing, reports, onOpenReport,
  onResetZoom, onZoomIn, onZoomOut,
  flashcardMode, showFlashcardIcons, revealedFlashcards, onToggleFlashcardMode, onToggleFlashcardIcons, onToggleFlashcard, onRevealFlashcard, onSetFlashcardStatus, flashcardStats,
  selectedConnection, onSelectConnection, onDeleteConnection, onColorChangeConnection, onStrokeChangeConnection, onLabelChangeConnection, onCreateConnection,
  postIts = [], onAddPostIt, onDeletePostIt, onPostItTextChange, onPostItColorChange, onPostItDragStart, onPostItResizeStart, onPostItExitPanMode,
  zoom = 1,
  isMobile = false,
  isAILoading = false,
}) => {
  const { connectState, startConnect, handleMouseMove, handleMouseUp } =
    useConnectInteraction(scrollContainerRef, nodes, onCreateConnection, zoom);

  const t = useT();
  const supportsPointer = typeof window !== 'undefined' && 'PointerEvent' in window;

  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, nodeId: null });
  const [exportSubOpen, setExportSubOpen] = useState(false);
  const [flashcardSubOpen, setFlashcardSubOpen] = useState(false);
  const [activeStudyCardId, setActiveStudyCardId] = useState(null);

  const effectiveStudyCardId = flashcardMode ? activeStudyCardId : null;
  const activeStudyCard = nodes.find(node => node.id === effectiveStudyCardId && node.isFlashcard) || null;
  const activeStudyCardRevealed = activeStudyCard ? Boolean(revealedFlashcards[activeStudyCard.id]) : false;
  const activeStudyFront = activeStudyCard
    ? maskTitleTerms((activeStudyCard.flashcardData?.front || activeStudyCard.summary || activeStudyCard.title || '').replace(/{[^}]+}/g, '____'), activeStudyCard.title)
    : '';
  const activeStudyBack = activeStudyCard
    ? stripFlashcardMarkers(activeStudyCard.flashcardData?.back || activeStudyCard.description || activeStudyCard.title || '')
    : '';
  const activeStudySummary = activeStudyCard
    ? stripFlashcardMarkers(activeStudyCard.summary || '')
    : '';

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useMobileInteractions({
    isMobile,
    onLongPress: (e) => {
      const touch = e.touches[0];
      handleContextMenu({
        preventDefault: () => {},
        stopPropagation: () => {},
        clientX: touch.clientX,
        clientY: touch.clientY
      });
    },
    onDoubleTap: () => onResetZoom()
  });

  useEffect(() => {
    if (!contextMenu.visible) return;
    const close = () => { 
      setContextMenu(prev => ({ ...prev, visible: false, nodeId: null })); 
      setExportSubOpen(false); 
      setFlashcardSubOpen(false);
    };
    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
    };
  }, [contextMenu.visible]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = scrollContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const canvasX = (e.clientX - rect.left + container.scrollLeft) / zoom;
    const canvasY = (e.clientY - rect.top + container.scrollTop) / zoom;
    
    // Check if we clicked on a node (rect bounds)
    const clickedNode = nodes.find(n => {
      const W = n.type === 'problem' ? NODE_W_ROOT : NODE_W;
      const H = n.type === 'problem' ? NODE_H_ROOT : NODE_H;
      return Math.abs(n.x - canvasX) <= W / 2 + 8 && Math.abs(n.y - canvasY) <= H / 2 + 8;
    });

    setContextMenu({ 
      visible: true, 
      x: e.clientX, 
      y: e.clientY, 
      canvasX, 
      canvasY,
      nodeId: clickedNode?.id || null
    });
  };

  const handlePointerCanvasDown = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    onCanvasMouseDown(e);
  };

  const handlePointerMove = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    handleMouseMove(e);
  };

  const handlePointerUp = (e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    handleMouseUp();
  };

  return (
    <div className="flex-1 relative overflow-hidden" style={{ background: 'var(--canvas-bg)' }}>
      <div
        ref={scrollContainerRef}
        className="w-full h-full overflow-auto"
        onMouseDown={!supportsPointer ? onCanvasMouseDown : undefined}
        onPointerDown={supportsPointer ? handlePointerCanvasDown : undefined}
        onContextMenu={!isMobile ? handleContextMenu : undefined}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: isPanMode ? 'grab' : selectionState.isSelecting ? 'crosshair' : 'default',
          touchAction: 'manipulation',
        }}
      >
        <div style={{ width: CANVAS_SIZE * zoom, height: CANVAS_SIZE * zoom, position: 'relative' }}>
        <svg
          ref={svgRef}
          width={CANVAS_SIZE} height={CANVAS_SIZE}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0, background: 'var(--canvas-bg)' }}
          onMouseMove={!supportsPointer ? handleMouseMove : undefined}
          onMouseUp={!supportsPointer ? handleMouseUp : undefined}
          onMouseLeave={!supportsPointer ? handleMouseUp : undefined}
        onPointerMove={supportsPointer ? handlePointerMove : undefined}
        onPointerUp={supportsPointer ? handlePointerUp : undefined}
        onPointerLeave={supportsPointer ? handlePointerUp : undefined}
        onPointerCancel={supportsPointer ? handlePointerUp : undefined}
        onTouchMove={(e) => {
          if (supportsPointer) return;
          if (e.touches.length === 1) {
            handleMouseMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
          }
        }}
        onTouchEnd={() => {
          if (supportsPointer) return;
          handleMouseUp();
        }}
        >

          <g id="connections-layer">
            {connections.map((conn) => {
              const fromNode = nodes.find((n) => n.id === conn.from);
              const toNode = nodes.find((n) => n.id === conn.to);
              if (!fromNode || !toNode) return null;
              return (
                <ConnectionItem
                  key={conn.id}
                  conn={conn}
                  fromNode={fromNode}
                  toNode={toNode}
                  isSelected={selectedConnection?.id === conn.id}
                  onSelect={onSelectConnection}
                />
              );
            })}
          </g>

          {connectState.active && (
            <line
              x1={connectState.fromX} y1={connectState.fromY}
              x2={connectState.curX} y2={connectState.curY}
              stroke="var(--ctp-blue)" strokeWidth="2" opacity="0.8"
              style={{ pointerEvents: 'none' }}
            />
          )}

          {selectionState.isSelecting && (
            <rect
              x={Math.min(selectionState.startX, selectionState.endX)}
              y={Math.min(selectionState.startY, selectionState.endY)}
              width={Math.abs(selectionState.endX - selectionState.startX)}
              height={Math.abs(selectionState.endY - selectionState.startY)}
              fill="color-mix(in srgb, var(--ctp-lavender) 10%, transparent)" stroke="var(--ctp-lavender)" strokeWidth="2" strokeDasharray="5,5"
            />
          )}

          <g id="nodes-layer">
            {nodes.map((node) => (
              <NodeItem
                key={node.id}
                node={node}
                isSelected={selectedNode?.id === node.id}
                isMultiSelected={selectedNodes.includes(node.id)}
                isHovered={hoveredNode === node.id}
                selectedNode={selectedNode}
                connectState={connectState}
                startConnect={startConnect}
                onMouseDown={(e) => onNodeMouseDown(e, node.id)}
                onNodeClick={onNodeClick}
                onHover={onNodeHover}
                onToggleBan={onToggleBan}
                onFocusNode={onFocusNode}
                onMarkNode={onMarkNode}
                onEditNode={onEditNode}
                onColorNode={onColorNode}
                onSuggestSolutions={onSuggestSolutions}
                onDeleteNode={onDeleteNode}
                onStartInfoGathering={onStartInfoGathering}
                onAddNode={onAddNode}
                flashcardMode={flashcardMode}
                showFlashcardIcons={showFlashcardIcons}
                isRevealed={Boolean(revealedFlashcards[node.id])}
                onRevealFlashcard={onRevealFlashcard}
                onOpenStudyCard={setActiveStudyCardId}
                onSetFlashcardStatus={onSetFlashcardStatus}
                isMobile={isMobile}
                isAILoading={isAILoading}
                supportsPointer={supportsPointer}
              />
            ))}
          </g>

          <g id="postits-layer">
            {postIts.map(postIt => (
              <PostItItem
                key={postIt.id}
                postIt={postIt}
                onDragStart={onPostItDragStart}
                onDelete={onDeletePostIt}
                onTextChange={onPostItTextChange}
                onColorChange={onPostItColorChange}
                onResizeStart={onPostItResizeStart}
                onExitPanMode={onPostItExitPanMode}
                supportsPointer={supportsPointer}
              />
            ))}
          </g>

          {selectedConnection && (() => {
            const fromNode = nodes.find((n) => n.id === selectedConnection.from);
            const toNode = nodes.find((n) => n.id === selectedConnection.to);
            if (!fromNode || !toNode) return null;
            return (
              <ConnectionOverlay
                conn={selectedConnection}
                fromNode={fromNode}
                toNode={toNode}
                onDelete={onDeleteConnection}
                onColorChange={onColorChangeConnection}
                onStrokeChange={onStrokeChangeConnection}
                onLabelChange={onLabelChangeConnection}
              />
            );
          })()}
        </svg>
        </div>
      </div>

      <CanvasToolbar
        selectedNodes={selectedNodes}
        selectedNode={selectedNodes.length === 1 ? (nodes.find(n => n.id === selectedNodes[0]) ?? null) : null}
        activeParentNode={activeParentNode}
        isPanMode={isPanMode}
        onTogglePanMode={onTogglePanMode}
        onAddNode={onAddNode}
        onEditNode={onEditNode}
        onMarkNode={onMarkNode}
        onColorNode={onColorNode}
        onToggleBan={onToggleBan}
        onDeleteNode={onDeleteNode}
        onBulkColorChange={onBulkColorChange}
        onBulkToggleBan={onBulkToggleBan}
        onBulkDelete={onBulkDelete}
        onAnalyzeMap={onAnalyzeMap}
        onShareMap={onShareMap}
        hasNodes={hasNodes}
        isAnalyzing={isAnalyzing}
        reports={reports}
        onOpenReport={onOpenReport}
        flashcardMode={flashcardMode}
        onToggleFlashcardMode={onToggleFlashcardMode}
        flashcardStats={flashcardStats}
        isMobile={isMobile}
      />

      <Modal
        isOpen={!!activeStudyCard}
        onClose={() => setActiveStudyCardId(null)}
        title={activeStudyCardRevealed ? activeStudyCard?.title : t('editNode.flashcard')}
        badge={activeStudyCard?.flashcardData?.type === 'cloze' ? 'Cloze' : activeStudyCard?.flashcardData?.type}
        badgeClass="px-3 py-1 rounded-full text-xs font-medium"
      >
        {activeStudyCard && (
          <div className="space-y-4">
            <button
              onClick={() => { if (!activeStudyCardRevealed) onRevealFlashcard(activeStudyCard.id); }}
              className="w-full text-left rounded-[20px] p-5 transition-all active:scale-[0.99]"
              style={{
                background: 'var(--surface)',
                border: `1px solid var(--border)`,
                cursor: activeStudyCardRevealed ? 'default' : 'pointer',
              }}
            >
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                {activeStudyCardRevealed ? t('editNode.flashcardAnswer') : t('editNode.flashcardFront')}
              </div>
              <div className="text-[21px] font-semibold leading-[1.35]" style={{ color: 'var(--text-primary)' }}>
                {activeStudyCardRevealed ? activeStudyBack : activeStudyFront}
              </div>
              {!activeStudyCardRevealed && (
                <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  {t('editNode.flashcardTap')}
                </div>
              )}
            </button>

            {activeStudyCardRevealed && activeStudySummary && (
              <div className="rounded-[18px] px-4 py-3" style={{ background: 'var(--surface)', border: '1px solid var(--border-s)' }}>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  {t('editNode.flashcardBack')}
                </div>
                <div className="text-[14px] leading-[1.5]" style={{ color: 'var(--text-muted)' }}>
                  {activeStudySummary}
                </div>
              </div>
            )}

            {activeStudyCardRevealed && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { onSetFlashcardStatus(activeStudyCard.id, 'known'); setActiveStudyCardId(null); }}
                  className="rounded-[16px] px-4 py-3 text-[12px] font-bold uppercase tracking-[0.14em]"
                  style={{ background: 'color-mix(in srgb, var(--success) 18%, transparent)', border: '1px solid color-mix(in srgb, var(--success) 35%, transparent)', color: 'var(--success)' }}
                >
                  {t('editNode.flashcardKnown')}
                </button>
                <button
                  onClick={() => { onSetFlashcardStatus(activeStudyCard.id, 'review'); setActiveStudyCardId(null); }}
                  className="rounded-[16px] px-4 py-3 text-[12px] font-bold uppercase tracking-[0.14em]"
                  style={{ background: 'color-mix(in srgb, var(--warning) 18%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 35%, transparent)', color: 'var(--warning)' }}
                >
                  {t('editNode.flashcardReview')}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {contextMenu.visible && (
        <div
          style={{
            position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 100,
            background: 'color-mix(in srgb, var(--bg-dim) 96%, transparent)',
            border: '1px solid var(--border)',
            borderRadius: '14px',
            minWidth: '180px',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}
          onContextMenu={e => e.preventDefault()}
        >
          {contextMenu.nodeId ? (
            <>
              <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-dim)', borderBottom: '1px solid var(--border-s)', fontFamily: 'var(--font-mono)' }}>
                {t('canvas.nodeOptions')}
              </div>
              <button
                onClick={() => { onToggleFlashcard(contextMenu.nodeId); setContextMenu(prev => ({ ...prev, visible: false, nodeId: null })); }}
                className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3"
                style={{ color: 'var(--text-primary)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span>🎴</span> {nodes.find(n => n.id === contextMenu.nodeId)?.isFlashcard ? t('canvas.removeFlashcard') : t('canvas.setFlashcard')}
              </button>
              <button
                onClick={() => { const node = nodes.find(n => n.id === contextMenu.nodeId); if (node) onStartInfoGathering(node); setContextMenu(prev => ({ ...prev, visible: false, nodeId: null })); }}
                className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3"
                style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-s)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span>🔍</span> {t('canvas.deepenWithAI')}
              </button>
              <div style={{ height: '1px', background: 'var(--border)', margin: '3px 12px' }} />
            </>
          ) : (
            <>
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setFlashcardSubOpen(v => !v); setExportSubOpen(false); }}
                  className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center justify-between gap-3"
                  style={{ color: 'var(--text-primary)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span className="flex items-center gap-3"><span>🎴</span> {t('canvas.flashcard')}</span>
                  <span style={{ color: 'var(--text-dim)', fontSize: '10px' }}>{flashcardSubOpen ? '▼' : '▶'}</span>
                </button>
                {flashcardSubOpen && (
                  <div
                    className="absolute top-0 left-full overflow-hidden"
                    style={{
                      background: 'color-mix(in srgb, var(--bg-dim) 96%, transparent)',
                      border: '1px solid var(--border)',
                      borderRadius: '14px',
                      backdropFilter: 'blur(12px)',
                      minWidth: '200px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    }}
                  >
                    <button onClick={() => { onToggleFlashcardMode(); setContextMenu(prev => ({ ...prev, visible: false })); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span>{flashcardMode ? '🎓' : '🧠'}</span> {flashcardMode ? t('canvas.exitMode') : t('canvas.activateMode')}
                    </button>
                    <button onClick={() => { onToggleFlashcardIcons(); setContextMenu(prev => ({ ...prev, visible: false })); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span>{showFlashcardIcons ? '🙈' : '👁️'}</span> {showFlashcardIcons ? t('canvas.hideIcons') : t('canvas.showIcons')}
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => { onAddPostIt(contextMenu.canvasX - 100, contextMenu.canvasY - 70); setContextMenu(prev => ({ ...prev, visible: false })); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span>📝</span> {t('canvas.postIt')}
              </button>
            </>
          )}

          <button onClick={() => { onCenterCanvas(); setContextMenu(prev => ({ ...prev, visible: false, nodeId: null })); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span>🎯</span> {t('canvas.centerMap')}
          </button>
          <div style={{ borderTop: '1px solid var(--border-s)', padding: '8px 16px 6px' }}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>{t('canvas.zoom')}</div>
            <div className="flex items-center gap-1">
              <button onClick={(e) => { e.stopPropagation(); onZoomOut(); }} className="flex-1 py-1.5 text-[15px] font-bold rounded-lg transition-colors" style={{ color: 'var(--text-primary)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>−</button>
              <button onClick={(e) => { e.stopPropagation(); onResetZoom({ centerMap: true }); setContextMenu(prev => ({ ...prev, visible: false, nodeId: null })); }} className="flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-colors" style={{ color: 'var(--text-primary)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>100%</button>
              <button onClick={(e) => { e.stopPropagation(); onZoomIn(); }} className="flex-1 py-1.5 text-[15px] font-bold rounded-lg transition-colors" style={{ color: 'var(--text-primary)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>+</button>
            </div>
          </div>
          <div style={{ height: '1px', background: 'var(--border)', margin: '3px 12px' }} />
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); setExportSubOpen(v => !v); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center justify-between gap-3" style={{ color: 'var(--text-primary)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span className="flex items-center gap-3"><span>📤</span> {t('canvas.export')}</span>
              <span style={{ color: 'var(--text-dim)', fontSize: '10px' }}>{exportSubOpen ? '▼' : '▶'}</span>
            </button>
            {exportSubOpen && (
              <div className="absolute top-0 left-full overflow-hidden" style={{ background: 'color-mix(in srgb, var(--bg-dim) 96%, transparent)', border: '1px solid var(--border)', borderRadius: '14px', backdropFilter: 'blur(12px)', minWidth: '160px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                <button onClick={() => { onExportPNG(); setContextMenu(prev => ({ ...prev, visible: false })); setExportSubOpen(false); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><span>🖼️</span> {t('canvas.png')}</button>
                <button onClick={() => { onExportMermaid(); setContextMenu(prev => ({ ...prev, visible: false })); setExportSubOpen(false); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><span>📊</span> {t('canvas.mermaid')}</button>
                <button onClick={() => { onExportJSON(); setContextMenu(prev => ({ ...prev, visible: false })); setExportSubOpen(false); }} className="w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors flex items-center gap-3" style={{ color: 'var(--text-primary)', borderTop: '1px solid var(--border-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><span>📄</span> {t('canvas.json')}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
